import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogEnvelope } from '../types/catalog-service.api.types';
import type { RunProgress, RunRead, RunSummary, StageMetadataEntry } from '../types/run.api.types';
import { unwrap } from '../utils/envelope';

/** One run's record (name, status, connector, flow, config) — the run header's source. */
export async function getRun(runId: string): Promise<RunRead> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.byId(runId))
    .json<CatalogEnvelope<RunRead>>();
  return unwrap(body);
}

/** Most ids one `GET catalog-runs/summaries` accepts (51+ or none is a 422). */
export const MAX_RUN_SUMMARY_IDS = 50;

/**
 * Up to 50 runs as product-history rows show them (§89), in request order; an unknown or
 * other-tenant id is simply absent. DB-only server-side, so one call replaces 2 reads per row.
 */
export async function getRunSummaries(runIds: readonly string[]): Promise<RunSummary[]> {
  const searchParams = new URLSearchParams();
  for (const id of runIds) searchParams.append('ids', id);
  const body = await catalogServiceClient
    .get(endpoints.catalogService.runs.summaries(), { searchParams })
    .json<CatalogEnvelope<RunSummary[]>>();
  return unwrap(body);
}

/**
 * The backend's own one-line description per stage (B1) — the "How this flow works" panel
 * prefers these over its local copy; display names still come from this app's labels file.
 */
export async function getStageMetadata(): Promise<StageMetadataEntry[]> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.stageMetadata())
    .json<CatalogEnvelope<StageMetadataEntry[]>>();
  return unwrap(body);
}

/** Live position of a run: stages, open gate, totals, retry options, delivery. */
export async function getRunProgress(runRef: string): Promise<RunProgress> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.progress(runRef))
    .json<CatalogEnvelope<RunProgress>>();
  return unwrap(body);
}
